import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axios from 'axios';
import { subMonths, subYears, parseISO, format } from 'date-fns';
import {
  formatAnalyticsReferenceDateLong,
  getAnalyticsReferenceDateParams,
  isValidAnalyticsReferenceDate,
  todayLocalYmd,
  useAnalyticsReferenceDate,
} from '../contexts/AnalyticsReferenceDateContext';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { getApiErrorMessage } from '../utils/apiError';
import {
  formatMeasureValue,
  isMeasurableKind,
} from '../components/HabitQuickLogChips';
import HabitExportDialog from '../components/HabitExportDialog';
import MetricInfo from '../components/MetricInfo';
import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { periodNoun, streakUnitNoun } from '../metrics/streakFormat';
import {
  GranularityOption,
  HealthLens,
  HealthLensSummary,
  LENS_CONFIG,
  LENS_ORDER,
  RANGE_LABELS,
  RANGE_ORDER,
  RANGE_SPAN_DAYS,
  RangeOption,
} from './habitStatsTypes';
import {
  useHabitStatsDefaultLens,
  useHabitStatsRangeSettings,
} from '../hooks/useHabitStatsPreferences';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// --- Types ---

interface RollingAverage {
  days: number;
  completion_rate: number;
  completed_count: number;
  possible_count: number;
}

interface DayOfWeekStat {
  day_index: number;
  day_name: string;
  completion_rate: number;
  total_logged: number;
  total_possible: number;
}

interface PeriodComparison {
  // Null when the habit's cycle is longer than (or unaligned with) the
  // comparison window — there is no honest number, so none is sent.
  current: number | null;
  previous: number | null;
  delta: number | null;
  applicable: boolean;
  // closed_periods | period_progress | not_applicable — see the backend
  // PeriodComparison docstring for what each reading means.
  basis: string;
  reason: string | null;
}

interface HabitStatsData {
  habit_id: number;
  habit_title: string;
  target_count: number;
  recurrence_type: string;
  recurrence_interval?: number;
  rolling_averages: RollingAverage[];
  streak_current: number;
  streak_longest: number;
  total_completions: number;
  total_tracked_days: number;
  total_tracked_periods?: number;
  consistency_score: number;
  day_of_week: DayOfWeekStat[];
  week_comparison: PeriodComparison;
  month_comparison: PeriodComparison;
}

interface HabitHealthPoint {
  period: string;
  score: number;
  completed_count: number;
  target_count: number;
  eligible_periods: number;
  closed: boolean;
  completed_value?: number | null;
  target_value?: number | null;
}

interface HabitHealthResponse {
  habit_id: number;
  algorithm: HealthLens;
  range: string;
  granularity: string;
  requested_granularity: string;
  granularity_adjusted: boolean;
  start_date: string;
  end_date: string;
  as_of_date?: string | null;
  summary: HealthLensSummary;
  data: HabitHealthPoint[];
}

// Measurement metadata from GET /api/habits/{id} (migration 0022): the stats
// endpoints stay measurement-agnostic, so the display unit is joined from the
// habit itself. unit_label is the ONLY display unit — the legacy `unit` field
// is import provenance and must never be rendered.
interface HabitMeasurementMeta {
  measurement_kind?: string;
  unit_label?: string | null;
  target_value?: number | null;
}

type CustomMode = 'dates' | 'months' | 'years';

const recurrenceLabel: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  custom: 'Custom',
};

const granularityNoun: Record<string, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  period: 'Period',
};

const finiteNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

interface ResolvedComparison {
  applicable: boolean;
  basis: string;
  reason: string | null;
  current: number | null;
  previous: number | null;
  delta: number | null;
}

/**
 * Normalize a comparison for rendering, keeping nulls null.
 *
 * A missing value must NOT become 0% — the backend deliberately sends null
 * when no honest comparison exists (cycle longer than the window), and
 * showing 0% there is exactly the false negative the semantics fix removed.
 * Older responses without ``applicable`` are treated as applicable so a
 * cached bundle keeps rendering.
 */
const buildComparison = (raw?: PeriodComparison | null): ResolvedComparison => {
  const applicable = raw ? raw.applicable !== false && raw.current != null : false;
  return {
    applicable,
    basis: raw?.basis || 'closed_periods',
    reason: raw?.reason ?? null,
    current: raw?.current == null ? null : clampPercent(raw.current),
    previous: raw?.previous == null ? null : clampPercent(raw.previous),
    delta: raw?.delta == null ? null : finiteNumber(raw.delta),
  };
};

const clampPercent = (value: unknown): number =>
  Math.max(0, Math.min(100, finiteNumber(value)));

const defaultGranularityFor = (stats: HabitStatsData): GranularityOption => {
  const interval = stats.recurrence_interval ?? 1;
  if (stats.recurrence_type === 'daily' && interval === 1) return 'day';
  return 'period';
};

const daysBetween = (start: string, end: string): number =>
  Math.max(1, Math.round((Date.parse(end) - Date.parse(start)) / 86400000) + 1);

// Defensive density guard mirroring the backend: never request an unusably
// dense chart, especially on mobile. day/period ~ one point per day.
const clampGranularityToDensity = (
  g: GranularityOption,
  days: number,
  mobile: boolean,
): GranularityOption => {
  const cap = mobile ? 62 : 200;
  const points = g === 'week' ? days / 7 : g === 'month' ? days / 30 : days;
  if (points <= cap) return g;
  if (g === 'day' || g === 'period') return days / 7 <= cap ? 'week' : 'month';
  if (g === 'week') return 'month';
  return g;
};

// --- Component ---

const HabitStats: React.FC = () => {
  const {
    cardContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
  } = useLayoutTokens();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    asOfDate,
    enabled: analyticsReferenceDateEnabled,
    resetToToday,
  } = useAnalyticsReferenceDate();
  const { enabledRanges } = useHabitStatsRangeSettings();
  const { defaultLens } = useHabitStatsDefaultLens();

  const [stats, setStats] = useState<HabitStatsData | null>(null);
  const [habitMeta, setHabitMeta] = useState<HabitMeasurementMeta | null>(null);
  const [health, setHealth] = useState<HabitHealthResponse | null>(null);
  // Two domains: summary (loads once per habit, range/lens-independent) and
  // the health series (refetches on every range/lens/granularity change).
  // They share neither loading nor error state so a flaky chart never blanks
  // the page.
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeOption>('30d');
  const [granularity, setGranularity] = useState<GranularityOption>('day');
  const [healthLens, setHealthLens] = useState<HealthLens>(defaultLens);
  const [customMode, setCustomMode] = useState<CustomMode>('dates');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customN, setCustomN] = useState(6);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [statsRetryToken, setStatsRetryToken] = useState(0);
  const [healthRetryToken, setHealthRetryToken] = useState(0);
  const granularityTouchedRef = useRef(false);
  const clampNotedRef = useRef('');

  // Stable per-mount; timezone doesn't change mid-session.
  const tzOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const analyticsReferenceDateParams = useMemo(
    () => getAnalyticsReferenceDateParams(analyticsReferenceDateEnabled, asOfDate),
    [analyticsReferenceDateEnabled, asOfDate],
  );
  const analyticsReferenceDateLabel = useMemo(
    () => formatAnalyticsReferenceDateLong(asOfDate),
    [asOfDate],
  );

  // The latest date a custom range may end on: the reference date when active,
  // otherwise local today.
  const asOfCeiling = useMemo(
    () =>
      analyticsReferenceDateEnabled && isValidAnalyticsReferenceDate(asOfDate)
        ? (asOfDate as string)
        : todayLocalYmd(),
    [analyticsReferenceDateEnabled, asOfDate],
  );

  // Resolve the effective custom start/end. "Last N months/years" are computed
  // relative to the analytics ceiling using date-fns (which clamps Feb-29).
  const resolvedCustom = useMemo(() => {
    if (range !== 'custom') return { start: '', end: '' };
    if (customMode === 'dates') return { start: customStart, end: customEnd };
    const n = Math.max(1, Math.floor(customN || 1));
    const end = asOfCeiling;
    try {
      const base = parseISO(end);
      const startDate = customMode === 'months' ? subMonths(base, n) : subYears(base, n);
      return { start: format(startDate, 'yyyy-MM-dd'), end };
    } catch {
      return { start: '', end };
    }
  }, [range, customMode, customStart, customEnd, customN, asOfCeiling]);

  const customError = useMemo<string | null>(() => {
    if (range !== 'custom') return null;
    const { start, end } = resolvedCustom;
    if (!start || !end) {
      return customMode === 'dates'
        ? 'Choose a start and end date.'
        : 'Enter a positive number.';
    }
    if (!isValidAnalyticsReferenceDate(start) || !isValidAnalyticsReferenceDate(end)) {
      return 'Dates must be valid calendar dates (YYYY-MM-DD).';
    }
    if (start > end) return 'Start date must be on or before the end date.';
    if (
      analyticsReferenceDateEnabled &&
      isValidAnalyticsReferenceDate(asOfDate) &&
      end > (asOfDate as string)
    ) {
      return `End date must be on or before the reference date (${asOfDate}).`;
    }
    return null;
  }, [range, resolvedCustom, customMode, analyticsReferenceDateEnabled, asOfDate]);

  const customValid = range !== 'custom' || customError === null;

  const spanDays = useMemo(() => {
    if (range === 'custom') {
      const { start, end } = resolvedCustom;
      return customValid && start && end ? daysBetween(start, end) : 30;
    }
    if (range === 'all') return Math.max(1, stats?.total_tracked_days ?? 3650);
    return RANGE_SPAN_DAYS[range];
  }, [range, resolvedCustom, customValid, stats]);

  const effectiveGranularity = useMemo(
    () => clampGranularityToDensity(granularity, spanDays, isMobile),
    [granularity, spanDays, isMobile],
  );

  const recordUsage = useCallback((event: string) => {
    // Fire-and-forget coarse usage counter; failures are non-fatal.
    axios.post(`${API_BASE_URL}/api/habits/stats/usage`, { event }).catch(() => {});
  }, []);

  // Summary: depends only on habit id + timezone/reference date.
  const fetchStats = useCallback(
    async (signal: AbortSignal) => {
      try {
        setStatsLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/habits/${id}/stats`, {
          params: { tz_offset: tzOffset, ...analyticsReferenceDateParams },
          signal,
        });
        if (!res.data || !Array.isArray(res.data.rolling_averages)) {
          setStatsError('Habit statistics response was missing required fields.');
          setStats(null);
          return;
        }
        setStats(res.data as HabitStatsData);
        setStatsError(null);
      } catch (err: any) {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
        setStatsError(getApiErrorMessage(err, 'Failed to load habit statistics'));
      } finally {
        setStatsLoading(false);
      }
    },
    [id, tzOffset, analyticsReferenceDateParams],
  );

  // Health series: depends on habit id, lens, range, granularity, custom
  // dates, timezone/reference date.
  const fetchHealth = useCallback(
    async (signal: AbortSignal) => {
      if (range === 'custom' && !customValid) {
        // Keep the last good chart; the inline warning explains the pause.
        setHealthLoading(false);
        return;
      }
      try {
        setHealthLoading(true);
        const params: Record<string, string | number> = {
          algorithm: healthLens,
          range,
          granularity: effectiveGranularity,
          tz_offset: tzOffset,
          ...analyticsReferenceDateParams,
        };
        if (range === 'custom') {
          params.start_date = resolvedCustom.start;
          params.end_date = resolvedCustom.end;
        }
        const res = await axios.get(`${API_BASE_URL}/api/habits/${id}/stats/health`, {
          params,
          signal,
        });
        if (!res.data || !Array.isArray(res.data.data) || !res.data.summary) {
          setHealthError('Health response was missing required fields.');
          setHealth(null);
          return;
        }
        setHealth(res.data as HabitHealthResponse);
        setHealthError(null);
      } catch (err: any) {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
        setHealthError(getApiErrorMessage(err, 'Failed to load habit health data'));
      } finally {
        setHealthLoading(false);
      }
    },
    [
      id,
      healthLens,
      range,
      effectiveGranularity,
      tzOffset,
      analyticsReferenceDateParams,
      resolvedCustom,
      customValid,
    ],
  );

  // Measurement metadata (kind + unit label). Progressive enhancement only:
  // a failure just means values render unitless, so no error state.
  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${API_BASE_URL}/api/habits/${id}`, { signal: controller.signal })
      .then((res) => {
        setHabitMeta(
          res.data && typeof res.data === 'object'
            ? (res.data as HabitMeasurementMeta)
            : null,
        );
      })
      .catch(() => {});
    return () => controller.abort();
  }, [id]);

  // Two independent effects, each with its own AbortController so a rapid
  // range/lens/granularity toggle can't race stale responses into state.
  useEffect(() => {
    const controller = new AbortController();
    fetchStats(controller.signal);
    return () => controller.abort();
  }, [fetchStats, statsRetryToken]);

  useEffect(() => {
    const controller = new AbortController();
    fetchHealth(controller.signal);
    return () => controller.abort();
  }, [fetchHealth, healthRetryToken]);

  // Auto-select a sensible granularity per range span until the user overrides
  // it. <=180d -> recurrence default (day for daily, period otherwise);
  // 181d..3y -> week; >3y/Lifetime -> month.
  useEffect(() => {
    if (granularityTouchedRef.current || !stats) return;
    let next: GranularityOption;
    if (spanDays <= 180) {
      next = defaultGranularityFor(stats);
    } else if (spanDays <= 1095) {
      next = 'week';
    } else {
      next = 'month';
    }
    setGranularity((prev) => (prev === next ? prev : next));
  }, [stats, range, spanDays]);

  // Coarse, one-shot usage counter when a density clamp actually changes the
  // rendered granularity.
  useEffect(() => {
    if (health && health.granularity !== granularity) {
      const key = `${range}:${granularity}->${health.granularity}`;
      if (clampNotedRef.current !== key) {
        clampNotedRef.current = key;
        recordUsage('granularity_clamped');
      }
    }
  }, [health, granularity, range, recordUsage]);

  // Page-level gate: only the summary (not the chart) decides whether we can
  // render anything at all. A chart-only failure shows inline below.
  if (statsLoading) {
    return (
      <PageShell maxWidth="wide">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (statsError || !stats) {
    return (
      <PageShell maxWidth="wide">
        <Box sx={{ mt: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/habits')}>
            Back to Habits
          </Button>
          {statsError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setStatsRetryToken((value) => value + 1)}
                >
                  Retry
                </Button>
              }
            >
              {statsError}
            </Alert>
          )}
        </Box>
      </PageShell>
    );
  }

  const primaryColor = theme.palette.primary.main;
  const successColor = theme.palette.success.main;
  const warningColor = theme.palette.warning.main;
  const rollingAverages = Array.isArray(stats.rolling_averages) ? stats.rolling_averages : [];
  const dayOfWeekStats = Array.isArray(stats.day_of_week) ? stats.day_of_week : [];
  const isDailyRate =
    stats.recurrence_type === 'daily' && (stats.recurrence_interval ?? 1) === 1;
  const streakUnit = streakUnitNoun(
    stats.recurrence_type,
    stats.recurrence_interval ?? 1,
    2,
  );
  const weekComparison = buildComparison(stats.week_comparison);
  const monthComparison = buildComparison(stats.month_comparison);
  const consistencyScore = clampPercent(stats.consistency_score);

  // Consistency donut data.
  const donutData = [
    { name: 'Score', value: consistencyScore },
    { name: 'Remaining', value: Math.max(0, 100 - consistencyScore) },
  ];

  // Radar chart data — glossary-registered percentage metrics only, since
  // every axis shares one honest 0-100 domain. Streak is a count of periods,
  // not a rate: the old `streak × 10` axis saturated at 10 periods and
  // punished long-cycle habits, so streaks live in their own cards instead.
  const avg7 = clampPercent(rollingAverages.find((a) => a.days === 7)?.completion_rate);
  const avg30 = clampPercent(rollingAverages.find((a) => a.days === 30)?.completion_rate);
  const avg90 = clampPercent(rollingAverages.find((a) => a.days === 90)?.completion_rate);
  const radarData = [
    { metric: '7d Rate', value: avg7 },
    { metric: '30d Rate', value: avg30 },
    { metric: '90d Rate', value: avg90 },
    { metric: 'Consistency', value: consistencyScore },
  ];

  // Health series chart data. Every lens returns a 0-100 series, so a shared
  // percent axis is correct; the non-percentage figures (slope, volatility)
  // live in the summary, not the chart.
  const lensConfig = LENS_CONFIG[healthLens];
  const healthData = Array.isArray(health?.data) ? health!.data : [];
  const healthHasHistory = (health?.summary?.eligible_periods ?? 0) > 0;
  const chartData = healthData.map((point) => ({
    ...point,
    score: clampPercent(point.score),
  }));
  const summaryNote = health ? lensConfig.summaryNote?.(health.summary) ?? null : null;
  const chartNoun = granularityNoun[health?.granularity ?? granularity] ?? 'Period';

  // Measurable display metadata (0022). OPAQUE is deliberately excluded:
  // its values are never interpreted, so it keeps the count-based copy and
  // gets no unit anywhere.
  const isMeasurableHabit = !!(
    habitMeta && isMeasurableKind(habitMeta.measurement_kind)
  );
  const measurableUnit = isMeasurableHabit ? habitMeta?.unit_label ?? null : null;
  const measurableTargetLabel =
    isMeasurableHabit && habitMeta?.target_value && habitMeta.target_value > 0
      ? `${formatMeasureValue(habitMeta.target_value)}${
          measurableUnit ? ` ${measurableUnit}` : ''
        }`
      : null;

  const dayOfWeekHasActivity = dayOfWeekStats.some((entry) => entry.total_logged > 0);
  const dowChartData = dayOfWeekStats.map((entry) => ({
    ...entry,
    completion_rate: clampPercent(entry.completion_rate),
    total_logged: finiteNumber(entry.total_logged),
    total_possible: finiteNumber(entry.total_possible),
  }));

  // Day-of-week bar chart colors.
  const dowBarColor = (rate: number): string => {
    if (rate >= 75) return successColor;
    if (rate >= 40) return warningColor;
    return theme.palette.error.main;
  };

  const DeltaIcon: React.FC<{ delta: number }> = ({ delta }) => {
    if (delta > 0) return <TrendingUpIcon fontSize="small" color="success" />;
    if (delta < 0) return <TrendingDownIcon fontSize="small" color="error" />;
    return <TrendingFlatIcon fontSize="small" color="disabled" />;
  };

  /**
   * One period-comparison panel.
   *
   * Three readings, chosen server-side by how the habit's cycle relates to the
   * comparison window, so the sub-labels have to follow the basis rather than
   * always claiming "so far this week":
   *  - closed_periods  — share of the habit's periods that closed in the window
   *  - period_progress — progress toward this period's target at the same point
   *  - not_applicable  — cycle longer than / unaligned with the window
   */
  const ComparisonPanel: React.FC<{
    title: string;
    comparison: ResolvedComparison;
    windowNoun: 'week' | 'month';
  }> = ({ title, comparison, windowNoun }) => {
    const cycle = periodNoun(stats.recurrence_type, stats.recurrence_interval ?? 1);
    const isProgress = comparison.basis === 'period_progress';
    const currentLabel = isProgress
      ? `Progress so far this ${windowNoun}`
      : `This ${windowNoun} so far`;
    const previousLabel = isProgress
      ? `At this point last ${windowNoun}`
      : windowNoun === 'week'
      ? 'Same days last week'
      : 'Same dates last month';

    // The server parameterises two of the three not-applicable reasons with
    // the window unit (`period_exceeds_week`, `period_not_aligned_to_month`),
    // so they have to be matched by prefix. Testing for a bare
    // `period_not_aligned` never matched anything, which sent both the
    // unaligned case and the no-closed-periods case to a "longer than a
    // {week}" fallback that was untrue for each of them.
    const notApplicableReason = (reason?: string | null): string => {
      if (reason && reason.startsWith('period_exceeds')) {
        return `Not applicable — this habit's ${cycle} is longer than a ${windowNoun}.`;
      }
      if (reason && reason.startsWith('period_not_aligned')) {
        return `This habit's ${cycle} is the same length as a ${windowNoun} but does not line up with the calendar ${windowNoun}, so a ${windowNoun}-over-${windowNoun} comparison would be misleading.`;
      }
      if (reason === 'no_closed_periods_in_window') {
        return `Not applicable — no ${cycle} closed inside the current or previous ${windowNoun}, so there is nothing to compare yet.`;
      }
      return `Not applicable — a ${windowNoun}-over-${windowNoun} comparison does not fit this habit's ${cycle}.`;
    };

    return (
      <Paper sx={panelPaddingSx}>
        <Typography variant="h6" gutterBottom>
          {title}
          <MetricInfo metric={`${windowNoun}_comparison`} />
        </Typography>
        {!comparison.applicable ? (
          <Typography variant="body2" color="text.secondary">
            {notApplicableReason(comparison.reason)}
          </Typography>
        ) : (
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {currentLabel}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {comparison.current}%
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <DeltaIcon delta={comparison.delta ?? 0} />
              <Typography
                variant="body2"
                color={
                  (comparison.delta ?? 0) > 0
                    ? 'success.main'
                    : (comparison.delta ?? 0) < 0
                    ? 'error.main'
                    : 'text.secondary'
                }
                sx={{ fontWeight: 600 }}
              >
                {(comparison.delta ?? 0) > 0 ? '+' : ''}
                {comparison.delta ?? 0}pp
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                {previousLabel}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }} color="text.secondary">
                {comparison.previous}%
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  const availableRanges = RANGE_ORDER.filter((r) => enabledRanges[r]);

  return (
    <PageShell maxWidth="wide">
      {/* Header */}
      <Box sx={pageHeaderSx}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/habits')} sx={{ mb: 1 }}>
          Back to Habits
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {stats.habit_title}
            </Typography>
            <Chip
              label={recurrenceLabel[stats.recurrence_type] ?? stats.recurrence_type}
              color="primary"
              size="small"
            />
            <Chip
              label={`Target: ${measurableTargetLabel ?? stats.target_count}`}
              size="small"
              variant="outlined"
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
          >
            Export
          </Button>
        </Box>
      </Box>

      {analyticsReferenceDateEnabled && asOfDate && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={resetToToday}>
              Reset to Today
            </Button>
          }
        >
          Viewing analytics as of {analyticsReferenceDateLabel}. This does not affect habit
          logging.
        </Alert>
      )}

      {/* Section 1: Summary Cards */}
      <Grid container spacing={pageGridSpacing} sx={sectionGapSx}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={[cardContentPaddingSx, { textAlign: 'center' }]}>
              <WhatshotIcon color="error" sx={{ fontSize: 32 }} />
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.streak_current}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Streak ({streakUnit})
                <MetricInfo metric="current_streak" />
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={[cardContentPaddingSx, { textAlign: 'center' }]}>
              <EmojiEventsIcon color="warning" sx={{ fontSize: 32 }} />
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.streak_longest}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Longest Streak ({streakUnit})
                <MetricInfo metric="longest_streak" />
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={[cardContentPaddingSx, { textAlign: 'center' }]}>
              <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stats.total_completions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Completions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={[cardContentPaddingSx, { textAlign: 'center' }]}>
              <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 0.5 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={35}
                      startAngle={90}
                      endAngle={-270}
                      dataKey="value"
                      stroke="none"
                      isAnimationActive={false}
                    >
                      <Cell fill={primaryColor} />
                      <Cell fill={theme.palette.action.disabledBackground} />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {consistencyScore}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Consistency Score
                <MetricInfo metric="consistency_score" />
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section 2: Rolling Averages */}
      <Paper sx={[panelPaddingSx, sectionGapSx]}>
        <Typography variant="h6" gutterBottom>
          Rolling Averages
        </Typography>
        <Grid container spacing={pageGridSpacing}>
          {rollingAverages.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">No closed recurrence periods are available yet.</Alert>
            </Grid>
          )}
          {rollingAverages.map((avg) => (
            <Grid item xs={12} sm={6} md key={avg.days}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {clampPercent(avg.completion_rate)}%
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {avg.days}-day window
                  <MetricInfo metric="rolling_average" days={avg.days} />
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={clampPercent(avg.completion_rate)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: theme.palette.action.disabledBackground,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {finiteNumber(avg.completed_count)}/{finiteNumber(avg.possible_count)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Section 3: Health Trend Chart */}
      <Paper sx={[panelPaddingSx, sectionGapSx]}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6">Completion Trend</Typography>
            <Typography variant="body2" color="text.secondary">
              {lensConfig.description}
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Health lens</InputLabel>
              <Select
                value={healthLens}
                label="Health lens"
                onChange={(e) => {
                  const value = e.target.value as HealthLens;
                  setHealthLens(value);
                  recordUsage(`health_lens_selected:${value}`);
                }}
              >
                {LENS_ORDER.map((lens) => (
                  <MenuItem key={lens} value={lens}>
                    {LENS_CONFIG[lens].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Range</InputLabel>
              <Select
                value={availableRanges.includes(range) ? range : ''}
                label="Range"
                onChange={(e) => {
                  const value = e.target.value as RangeOption;
                  setRange(value);
                  recordUsage(`range_selected:${value}`);
                }}
              >
                {availableRanges.map((option) => (
                  <MenuItem key={option} value={option}>
                    {RANGE_LABELS[option]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Granularity</InputLabel>
              <Select
                value={granularity}
                label="Granularity"
                onChange={(e) => {
                  granularityTouchedRef.current = true;
                  const value = e.target.value as GranularityOption;
                  setGranularity(value);
                  recordUsage(`granularity_selected:${value}`);
                }}
              >
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="period">Period</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {range === 'custom' && (
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-start" mb={1}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Custom range</InputLabel>
              <Select
                value={customMode}
                label="Custom range"
                onChange={(e) => {
                  const value = e.target.value as CustomMode;
                  setCustomMode(value);
                  recordUsage(
                    value === 'months'
                      ? 'custom_last_n_months'
                      : value === 'years'
                      ? 'custom_last_n_years'
                      : 'custom_range_applied',
                  );
                }}
              >
                <MenuItem value="dates">Specific dates</MenuItem>
                <MenuItem value="months">Last N months</MenuItem>
                <MenuItem value="years">Last N years</MenuItem>
              </Select>
            </FormControl>
            {customMode === 'dates' ? (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  inputProps={{ max: asOfCeiling }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  inputProps={{ max: asOfCeiling }}
                  error={Boolean(customError)}
                  helperText={customError ?? ''}
                />
              </>
            ) : (
              <TextField
                size="small"
                type="number"
                label={customMode === 'months' ? 'Months' : 'Years'}
                value={customN}
                onChange={(e) =>
                  setCustomN(Math.max(1, Math.floor(Number(e.target.value) || 1)))
                }
                inputProps={{ min: 1, max: customMode === 'months' ? 600 : 100 }}
                sx={{ width: 130 }}
              />
            )}
          </Box>
        )}

        {range === 'custom' && customError && customMode !== 'dates' && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {customError}
          </Alert>
        )}

        {health && healthHasHistory && (
          <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap" mb={0.5}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {Math.round(clampPercent(health.summary.score))}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lensConfig.label}
            </Typography>
            {health.summary.measurable &&
              health.summary.fulfilled_value != null &&
              health.summary.target_value_total != null && (
                <Typography variant="body2" color="text.secondary">
                  {formatMeasureValue(health.summary.fulfilled_value)} /{' '}
                  {formatMeasureValue(health.summary.target_value_total)}
                  {measurableUnit ? ` ${measurableUnit}` : ''}
                </Typography>
              )}
            {summaryNote && <Chip size="small" variant="outlined" label={summaryNote} />}
            {health.summary.low_confidence && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`Limited history (need ≥ ${health.summary.min_sample_size})`}
              />
            )}
          </Box>
        )}
        {health && health.granularity !== granularity && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Showing {health.granularity} buckets to keep this range readable.
          </Typography>
        )}

        {healthError ? (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setHealthRetryToken((value) => value + 1)}
              >
                Retry
              </Button>
            }
          >
            {healthError}
          </Alert>
        ) : healthLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={260}>
            <CircularProgress size={28} />
          </Box>
        ) : chartData.length > 0 && healthHasHistory ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip
                formatter={(value: number, _name: string, item: any) => {
                  // Where the lens carries real measured amounts, the tooltip
                  // names them with the habit's unit_label (never the legacy
                  // `unit`). OPAQUE never reaches here — isMeasurableHabit
                  // excludes it, so its values stay uninterpreted.
                  const point = item?.payload as HabitHealthPoint | undefined;
                  if (
                    isMeasurableHabit &&
                    point &&
                    point.completed_value != null &&
                    point.target_value != null
                  ) {
                    return [
                      `${value}% — ${formatMeasureValue(point.completed_value)} / ${formatMeasureValue(
                        point.target_value,
                      )}${measurableUnit ? ` ${measurableUnit}` : ''}`,
                      lensConfig.label,
                    ];
                  }
                  return [`${value}%`, lensConfig.label];
                }}
                labelFormatter={(label: string) => `${chartNoun}: ${label}`}
              />
              <Area
                type="monotone"
                dataKey="score"
                stroke={primaryColor}
                fill={primaryColor}
                fillOpacity={0.2}
                strokeWidth={2}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No history for this lens in the selected range
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Section 4: Day-of-Week Breakdown + Radar */}
      <Grid container spacing={pageGridSpacing} sx={sectionGapSx}>
        <Grid item xs={12} md={6}>
          <Paper sx={[panelPaddingSx, { height: '100%' }]}>
            <Typography variant="h6" gutterBottom>
              Day-of-Week Breakdown
              <MetricInfo metric="day_of_week" />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isDailyRate ? 'Completion rate by scheduled weekday' : 'Logged completions by weekday'}
            </Typography>
            {dowChartData.length > 0 && dayOfWeekHasActivity ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={dowChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day_name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip
                    formatter={(value: number) => [
                      `${value}%`,
                      isDailyRate ? 'Completion Rate' : 'Share of Logs',
                    ]}
                  />
                  <Bar dataKey="completion_rate" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                    {dowChartData.map((entry, index) => (
                      <Cell key={index} fill={dowBarColor(entry.completion_rate)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">No logged completions yet</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={[panelPaddingSx, { height: '100%' }]}>
            <Typography variant="h6" gutterBottom>
              Habit Health Radar
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} />
                <Radar
                  dataKey="value"
                  stroke={primaryColor}
                  fill={primaryColor}
                  fillOpacity={0.3}
                  isAnimationActive={false}
                />
              </RadarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Section 5: Period Comparisons */}
      <Grid container spacing={pageGridSpacing} sx={sectionGapSx}>
        <Grid item xs={12} sm={6}>
          <ComparisonPanel
            title="This Week So Far vs Last Week"
            comparison={weekComparison}
            windowNoun="week"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ComparisonPanel
            title="This Month So Far vs Last Month"
            comparison={monthComparison}
            windowNoun="month"
          />
        </Grid>
      </Grid>


      {/* Section 6: Extra info */}
      <Paper sx={[panelPaddingSx, sectionGapSx]}>
        <Typography variant="body2" color="text.secondary">
          Tracking for {stats.total_tracked_days} days · Target:{' '}
          {measurableTargetLabel ?? stats.target_count} per{' '}
          {stats.recurrence_type === 'daily'
            ? 'day'
            : stats.recurrence_type === 'weekly'
            ? 'week'
            : stats.recurrence_type === 'monthly'
            ? 'month'
          : 'period'}
        </Typography>
      </Paper>

      <HabitExportDialog
        open={exportDialogOpen}
        habitIds={id ? [Number(id)] : []}
        title={`Export ${stats.habit_title}`}
        onClose={() => setExportDialogOpen(false)}
      />
    </PageShell>
  );
};

export default HabitStats;
